import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LockKeyhole, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { apiClient } from "@/lib/api";

export default function AdminLoginPage({ refreshAuth, authData, authLoading }) {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!authLoading && authData?.user) {
      navigate(authData.user.landing_route || "/review", { replace: true });
    }
  }, [authData, authLoading, navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      await apiClient.post("/auth/admin/login", { username, password });
      await refreshAuth();
      toast.success("Zalogowano do panelu wewnętrznego.");
      navigate("/review", { replace: true });
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Nie udało się zalogować do panelu wewnętrznego.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background px-4 py-10 text-foreground">
      <div className="mx-auto flex min-h-[75vh] max-w-5xl items-center justify-center">
        <div className="grid w-full gap-6 lg:grid-cols-[1fr_0.92fr]">
          <Card className="hidden border-border bg-card/90 lg:block">
            <CardContent className="flex h-full flex-col justify-between p-10">
              <div>
                <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/12 text-primary">
                  <ShieldCheck className="h-7 w-7" />
                </div>
                <h1 className="text-4xl font-semibold tracking-tight">Panel wewnętrzny Byteflow</h1>
                <p className="mt-4 max-w-xl text-base leading-relaxed text-muted-foreground">
                  Ta ścieżka jest przeznaczona wyłącznie do obsługi wewnętrznej. Strona główna pozostaje w pełni przygotowana dla aplikantów.
                </p>
              </div>
              <div className="rounded-3xl border border-border bg-background/70 p-6">
                <p className="text-xs uppercase tracking-[0.24em] text-primary/80">Zakres dostępu</p>
                <ul className="mt-4 space-y-3 text-sm text-muted-foreground">
                  <li>• obsługa zgłoszeń</li>
                  <li>• aktualizacja statusów</li>
                  <li>• edycja formularza</li>
                  <li>• działania operacyjne</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border bg-card shadow-[0_10px_30px_rgba(0,0,0,0.35)]">
            <CardHeader>
              <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/12 text-primary">
                <LockKeyhole className="h-5 w-5" />
              </div>
              <CardTitle className="text-2xl font-semibold">Logowanie wewnętrzne</CardTitle>
            </CardHeader>
            <CardContent>
              <form className="space-y-5" onSubmit={handleSubmit}>
                <div className="space-y-2">
                  <Label htmlFor="admin-username">Login</Label>
                  <Input
                    id="admin-username"
                    value={username}
                    onChange={(event) => setUsername(event.target.value)}
                    placeholder="Wpisz login"
                    autoComplete="username"
                    data-testid="admin-username-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="admin-password">Hasło</Label>
                  <Input
                    id="admin-password"
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    placeholder="Wpisz hasło"
                    autoComplete="current-password"
                    data-testid="admin-password-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="h-11 w-full"
                  disabled={submitting}
                  data-testid="admin-login-submit-button"
                >
                  {submitting ? "Logowanie..." : "Wejdź do panelu"}
                </Button>
                <Button type="button" variant="secondary" className="w-full" onClick={() => navigate("/")}>Wróć na stronę główną</Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
